import React, { useState } from "react";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OrderwerksLogo from "../../../assets/images/orderwerks/orderwerks-logo-grey.svg";
import OrderwerksStackedLogo from "../../../assets/images/orderwerks/stacked-logo-grey.svg";
import { SideNavLink } from "../../shared/SideNavLink/SideNavLink";
import INavItem from "../../../models/INavItem";
import IConnection_Token from "../../../models/IConnection_Token";
import "./SideNavigation.scss";
import UIService from "../../../services/UIService";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IUpdateTokenSettingsDTO from "../../../models/DTO/IUpdateTokenSettingsDTO";
import IOnUpdateTokenSettingsDTO from "../../../models/DTO/IOnUpdateTokenSettingsDTO";
import ITokenSettings from "../../../models/ITokenSettings";
import MenuIdEnum from "../../../models/MenuIdEnum";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { DraggableNavItem } from "./DraggableNavItem";
import { useAppContextNonNull } from "@contexts/AppContext";
import OwButton from "@components/shared/OwButton/OwButton";

type Props = {
    menuOpen: boolean;
    menuClass: string;
    menuIconMode: boolean;
    routes: INavItem[];
};

export const SideNavigation = (props: Props) => {
    const { userModel, supplierModel, socket } = useAppContextNonNull();

    const { supplierLogo } = UIService.getSupplierLogo(supplierModel, userModel);

    const [editMode, setEditMode] = useState(false);
    const [editableRoutes, setEditableRoutes] = useState<INavItem[]>([]);
    const [hasChanges, setHasChanges] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [activeId, setActiveId] = useState<string | null>(null);

    // Initialize editable routes when entering edit mode
    React.useEffect(() => {
        if (editMode && props.routes) {
            // Copy routes as-is, they already have menuId which we use as identifier
            setEditableRoutes([...props.routes]);
        }
    }, [editMode, props.routes]);

    // DnD Kit sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Prevent an error during logoff
    if (!userModel) {
        return null;
    }

    const toggleEditMode = () => {
        if (editMode && hasChanges) {
            // TODO: Show confirmation dialog before discarding changes
            setHasChanges(false);
        }
        setEditMode(!editMode);
    };

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id as string);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            setEditableRoutes((items) => {
                const oldIndex = items.findIndex((item) => item.menuId === active.id);
                const newIndex = items.findIndex((item) => item.menuId === over?.id);

                setHasChanges(true);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
        setActiveId(null);
    };

    const saveMenuOrder = async () => {
        if (isSaving) return;

        setIsSaving(true);

        try {
            // Get the current token that has the settings
            const currentToken = userModel.isSupplierUser ? userModel.supplierUserToken : userModel.vendorUserToken;

            if (!currentToken) {
                console.error("No token found to update settings");
                return;
            }

            // Convert editableRoutes to menu_id_order format
            const menu_id_order: Partial<Record<MenuIdEnum, { order: number; sub_menu_id_order?: any }>> = {};
            editableRoutes.forEach((route, index) => {
                if (route.menuId) {
                    menu_id_order[route.menuId] = {
                        order: index,
                        // TODO: Handle sub-menu ordering if route has sections
                    };
                }
            });

            // Prepare the updated settings with the new menu order
            const updatedSettings: ITokenSettings = {
                ...(currentToken.settings || {}),
                menu_id_order: menu_id_order as ITokenSettings["menu_id_order"],
                menu: undefined, // Remove the old menu array
            };

            const payload: IUpdateTokenSettingsDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                tokenGuid: currentToken.guid,
                settings: updatedSettings,
            };

            // Listen for the response
            socket.once(EndpointsEnum.TokenOnUpdateSettings, (response: IOnUpdateTokenSettingsDTO) => {
                setIsSaving(false);

                if (response.token) {
                    // Update the local token settings
                    if (currentToken) {
                        currentToken.settings = response.token.settings || updatedSettings;
                    }

                    setHasChanges(false);
                    setEditMode(false);

                    // TODO: Show success notification
                    console.log("Menu order saved successfully");
                } else {
                    // TODO: Show error notification
                    console.error("Failed to save menu order");
                }
            });

            // Emit the update request
            socket.emit(EndpointsEnum.TokenUpdateSettings, payload);
        } catch (error) {
            setIsSaving(false);
            console.error("Error saving menu order:", error);
        }
    };

    const cancelEdit = () => {
        setHasChanges(false);
        setEditMode(false);
        setEditableRoutes([]);
    };

    // Use editable routes in edit mode, otherwise use props.routes
    const currentRoutes = editMode ? editableRoutes : props.routes;

    // Get the active item being dragged
    const activeItem = activeId ? currentRoutes.find((item) => item.menuId === activeId) : null;

    const navLiItems = (currentRoutes || []).map((navItem) => {
        const itemId = navItem.menuId;

        if (editMode) {
            return (
                <DraggableNavItem
                    key={itemId}
                    id={itemId}
                    navItem={navItem}
                    user={userModel}
                    isSupplierUser={userModel.isSupplierUser}
                    supplierUserToken={userModel.supplierUserToken}
                    vendorUserToken={userModel.vendorUserToken as IConnection_Token}
                    menuIconMode={props.menuIconMode}
                />
            );
        }

        return (
            <SideNavLink
                key={itemId}
                user={userModel}
                isSupplierUser={userModel.isSupplierUser}
                supplierUserToken={userModel.supplierUserToken}
                vendorUserToken={userModel.vendorUserToken as IConnection_Token}
                navItem={navItem}
                navigated={() => {}}
                menuIconMode={props.menuIconMode}
                toggleEditMode={toggleEditMode}
            />
        );
    });

    // console.log("navLiItems: ", navLiItems);

    return (
        <div id={"sideBar"} className={(props.menuOpen ? props.menuClass + " .g-0" : "col-0 d-none") + " SideNavigation"}>
            <ul title={"Side Navigation"} className={"list-unstyled sideBarNav " + (props.menuIconMode ? "menuIconMode" : "")}>
                {editMode ? (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}>
                        <SortableContext items={editableRoutes.map((item) => item.menuId)} strategy={verticalListSortingStrategy}>
                            {navLiItems}
                        </SortableContext>
                        <DragOverlay>
                            {activeItem ? (
                                <div className="draggable-nav-item-overlay">
                                    <SideNavLink
                                        user={userModel}
                                        isSupplierUser={userModel.isSupplierUser}
                                        supplierUserToken={userModel.supplierUserToken}
                                        vendorUserToken={userModel.vendorUserToken as IConnection_Token}
                                        navItem={activeItem}
                                        navigated={() => {}}
                                        menuIconMode={props.menuIconMode}
                                    />
                                </div>
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                ) : (
                    navLiItems
                )}
                {supplierLogo && !props.menuIconMode ? (
                    <li className={"poweredByLogo"}>
                        <OwLabel title={"powered_by"} noBottomMargin={true} />
                        <img src={OrderwerksLogo} title={"Orderwerks"} />
                    </li>
                ) : null}
                {supplierLogo && props.menuIconMode ? (
                    <li className={"poweredByLogo"}>
                        <img src={OrderwerksStackedLogo} className={"stackedLogo"} title={"Orderwerks"} />
                    </li>
                ) : null}
            </ul>

            {/* Edit Mode Controls */}
            {!props.menuIconMode && (
                <div className="edit-mode-controls p-3 border-top">
                    {!editMode ? (
                        <OwButton
                            title="edit_menu"
                            fn={toggleEditMode}
                            leftIconClass="fas fa-edit"
                            className="btn-sm btn-outline-secondary w-100"
                        />
                    ) : (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="text-muted small">
                                    <OwLabel title={"drag_items_to_reorder"} />
                                </span>
                            </div>
                            <div className="d-flex gap-2">
                                <OwButton
                                    title={isSaving ? "saving" : "save"}
                                    fn={saveMenuOrder}
                                    leftIconClass="fas fa-check"
                                    className="btn-sm btn-success flex-fill"
                                    disabled={!hasChanges}
                                    active={isSaving}
                                    disableOnActive={true}
                                />
                                <OwButton
                                    title="cancel"
                                    fn={cancelEdit}
                                    leftIconClass="fas fa-times"
                                    className="btn-sm btn-secondary flex-fill"
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
